import { useCallback, useMemo, useState } from 'react';
import { PreloadJob } from '../../UnicronApp/preloader/PreloadJob';
import { makePreloadSlot, usePreloadSlot } from '../../UnicronApp/preloader/PreloadSlot';
import { usePromise } from '../../common/hooks/usePromise';
import { Fetch, useFetch } from '../../common/services/NetworkService';
import { AsyncResult } from '../../common/types/AsyncResult';
import { makeOffersUrl } from '../makeOffersUrl';
import { processOffersResponse } from '../processOffersResponse';
import { Offer, OfferType } from '../types';

export const useOffers = (options?: { skip?: boolean }): AsyncResult<Offer[]> => {
  const skip = options?.skip;
  const cached = usePreloadSlot(OfferSlot);
  const [generation, setGeneration] = useState(0);
  const fetch = useFetch();

  const promiseState = usePromise(
    useMemo(() => {
      if ((cached && generation === 0) || skip) {
        return null;
      } else {
        return fetchContentfulOffers(fetch);
      }
    }, [cached, fetch, generation, skip]),
  );

  const refetch = useCallback(() => setGeneration(it => it + 1), []);

  if ((cached && generation === 0) || skip) {
    return { data: cached, refetch, loading: false };
  } else {
    return {
      loading: promiseState.loading,
      error: promiseState.error,
      data: promiseState.data ?? [],
      refetch,
    };
  }
};

const fetchContentfulOffers = async (fetch: Fetch) => {
  try {
    const url = makeOffersUrl(false, [], OfferType.CONSUMER_OFFER);
    const fetched = await fetch(url);
    const json: unknown = await fetched.json();
    return await processOffersResponse(json);
  } catch (error) {
    return undefined;
  }
};

const OfferSlot = makePreloadSlot('CONTENTFUL_OFFERS', (): Offer[] | undefined => undefined);

export const preloadContentfulOffers = ({ services }: PreloadJob) =>
  services.logger.profile('Fetching contentful offers', async () => {
    const { cache, network } = services;
    const { fetch } = network;
    const existing = cache.get(OfferSlot);
    if (existing) return existing;
    const offers = await fetchContentfulOffers(fetch);
    cache.set(OfferSlot, offers);
    return offers;
  });
